/**
 * Rainfall pond leaderboard → CSV.
 *
 * Engineering teams take the result of a rainfall analysis into Excel
 * to combine with cost / specification spreadsheets. The on-screen
 * leaderboard is for triage; the export is for the deliverable. We
 * always include drained ponds (`drained=true`) so the export is the
 * complete picture — Excel filters can hide them after the fact.
 *
 * Storm-scale handling: every storm-derived column is multiplied /
 * divided by the user's current what-if multiplier through the central
 * `rainfallScaling` helper. The scaled multiplier itself isn't stored
 * in the CSV (the worker doesn't know about it) — but the column names
 * carry a `_scaled` suffix so a downstream reader knows the values are
 * scenario values, not the raw worker output.
 */

import { scaledPondMetrics } from './rainfallScaling';
import { isPondDrained } from './rainfallStyling';

export interface PondCsvFeature {
  id?: string | null;
  layerId?: string | null;
  properties?: Record<string, unknown> | null;
}

/** CSV column identifiers — order is also the on-disk header order. */
const COLUMNS = [
  'pond_id',
  'volume_m3',
  'surface_area_m2',
  'max_depth_m',
  'catchment_m2',
  'inflow_lps_scaled',
  'fill_time_s_scaled',
  'total_runoff_m3_scaled',
  'capture_ratio_pct',
  'spillover_m3_scaled',
  'terminal',
  'chain_head_pond_id',
  'chain_order',
  'drained',
  'low_point_z',
  'overflow_z',
  'edge_touching',
] as const;

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Quote a CSV cell. We always escape values that contain comma /
 * quote / newline; numeric / boolean cells go in unquoted to keep the
 * file lean.
 */
function csvCell(v: string | number | boolean | null | undefined): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '';
    return String(v);
  }
  // String: escape quotes by doubling, wrap in quotes if it contains
  // special chars. Pond IDs in the wild can include `,` so we always
  // quote string cells defensively.
  return `"${v.replace(/"/g, '""')}"`;
}

/** Round to N decimals; returns null when input is non-finite. */
function round(v: number | null | undefined, decimals: number): number | null {
  if (!isNumber(v)) return null;
  const m = Math.pow(10, decimals);
  return Math.round(v * m) / m;
}

/**
 * Build the CSV string for every pond in the supplied feature list,
 * using the active what-if scale. `gullies` is the per-pond drained
 * map keyed by pondId — same source the inspector & leaderboard read.
 *
 * Non-pond features (flow lines, watersheds) are skipped.
 *
 * `stormDurationH` is intentionally unused right now; we keep the
 * signature symmetric with the helper module's other consumers in
 * case a future column wants "overflows mid-storm" as a precomputed
 * boolean. Removing it later is a deliberate decision; leaving it
 * costs nothing.
 */
export function buildRainfallCsv(
  features: ReadonlyArray<PondCsvFeature>,
  gullies: Record<string, boolean>,
  scale: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _stormDurationH?: number | null,
): string {
  const rows: string[] = [];
  rows.push(COLUMNS.join(','));

  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>;
    if (props.kind !== 'pond') continue;
    const pondId = typeof props.pondId === 'string' ? props.pondId : '';
    if (!pondId) continue;
    const drained = isPondDrained(props, gullies);

    const scaled = scaledPondMetrics(props, scale);

    const captureRatioPct =
      scaled.captureRatio != null
        ? round(Math.min(1, Math.max(0, scaled.captureRatio)) * 100, 1)
        : null;

    const cells: Array<string | number | boolean | null> = [
      pondId,
      round(scaled.volumeM3, 4),
      round(scaled.surfaceAreaM2, 3),
      round(scaled.maxDepthM, 4),
      round(scaled.catchmentAreaM2, 2),
      round(scaled.inflowLs, 3),
      round(scaled.fillTimeS, 1),
      round(scaled.totalRunoffM3, 4),
      captureRatioPct,
      round(scaled.spilloverM3, 4),
      props.terminal === true,
      typeof props.chainHeadPondId === 'string' ? props.chainHeadPondId : null,
      isNumber(props.chainOrder) ? props.chainOrder : null,
      drained,
      isNumber(props.lowPointZ) ? round(props.lowPointZ, 3) : null,
      isNumber(props.overflowZ) ? round(props.overflowZ, 3) : null,
      props.edgeTouching === true,
    ];

    rows.push(cells.map(csvCell).join(','));
  }

  // Trailing newline so `cat`-style readers don't print "no newline at
  // end of file" warnings — small polish but Excel and `csv` libs
  // expect it.
  return rows.join('\n') + '\n';
}

/**
 * Sanitise a layer name into a filename. Mirrors the worker's
 * `processRainfallAnalysis/index.ts` slug rule (alpha-num + underscore,
 * trimmed to 60 chars) so a re-export of the same layer round-trips to
 * the same file basename.
 */
export function safeRainfallCsvFilename(layerName: string | null | undefined): string {
  const fallback = 'rainfall';
  const slug = (layerName ?? fallback).replace(/[^A-Za-z0-9]+/g, '_').slice(0, 60) || fallback;
  return `${slug}-ponds.csv`;
}
